/**
 * Página de Cadastro de Pacientes
 * Tela auxiliar para pré-cadastro de pacientes (NÃO aparece no menu)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme as useAppTheme } from '../../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  Tooltip,
  Pagination
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  WarningAmber as WarningAmberIcon
} from '@mui/icons-material';
import pacienteService from '../../services/pacienteService';

const LIMIT = 10;

const Pacientes = () => {
  const { user } = useAuth();
  const { isDarkMode } = useAppTheme();
  const planoAtual = user?.plano || user?.clinica?.plano || 'FREE';
  const navigate = useNavigate();
  const [pacientes, setPacientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [pesquisa, setPesquisa] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [formData, setFormData] = useState({
    nome: '',
    cpfCnpj: '',
    email: '',
    telefone: '',
    dataNascimento: '',
    cep: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
    observacoes: '',
    ativo: true
  });

  const carregarPacientes = useCallback(async (paginaAtual, busca) => {
    try {
      setLoading(true);
      const data = await pacienteService.listar({ page: paginaAtual, limit: LIMIT, busca });
      if (data && typeof data === 'object' && 'pacientes' in data) {
        setPacientes(data.pacientes);
        setTotal(data.total);
        setTotalPages(data.totalPages);
      } else {
        setPacientes(Array.isArray(data) ? data : []);
      }
      setError('');
    } catch (err) {
      setError('Erro ao carregar pacientes');
      setPacientes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Dispara quando muda de página (sem debounce)
  useEffect(() => {
    carregarPacientes(page, pesquisa);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Dispara quando muda a pesquisa (com debounce de 400ms), reseta para página 1
  useEffect(() => {
    const timer = setTimeout(() => {
      if (page !== 1) {
        setPage(1); // o effect de page vai chamar carregarPacientes
      } else {
        carregarPacientes(1, pesquisa);
      }
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pesquisa]);

  const handleOpenModal = (paciente = null) => {
    if (paciente) {
      setEditando(paciente);
      setFormData(paciente);
    } else {
      setEditando(null);
      setFormData({
        nome: '',
        cpfCnpj: '',
        email: '',
        telefone: '',
        dataNascimento: '',
        cep: '',
        logradouro: '',
        numero: '',
        complemento: '',
        bairro: '',
        cidade: '',
        estado: '',
        observacoes: '',
        ativo: true
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditando(null);
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;
    if (name === 'cpfCnpj') {
      const formatted = formatarCPFCNPJ(value);
      setFormData({ ...formData, [name]: formatted });
    } else if (name === 'telefone') {
      const formatted = formatarTelefone(value);
      setFormData({ ...formData, [name]: formatted });
    } else if (name === 'cep') {
      const numeros = value.replace(/\D/g, '').slice(0, 8);
      setFormData({ ...formData, cep: numeros });
      if (numeros.length === 8) {
        try {
          const response = await fetch(`https://viacep.com.br/ws/${numeros}/json/`);
          const data = await response.json();
          if (!data.erro) {
            setFormData((prev) => ({
              ...prev,
              logradouro: data.logradouro || '',
              bairro: data.bairro || '',
              cidade: data.localidade || '',
              estado: data.uf || '',
              cep: numeros
            }));
          }
        } catch (err) {
          // Silencia erro de busca de CEP
        }
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };
  // Máscara para telefone brasileiro (celular ou fixo)
  const formatarTelefone = (valor) => {
    const numeros = valor.replace(/\D/g, '').slice(0, 11);
    if (numeros.length === 0) return '';
    if (numeros.length <= 2) return `(${numeros}`;
    if (numeros.length <= 6) return `(${numeros.slice(0,2)}) ${numeros.slice(2)}`;
    if (numeros.length <= 10) return `(${numeros.slice(0,2)}) ${numeros.slice(2,6)}-${numeros.slice(6)}`;
    return `(${numeros.slice(0,2)}) ${numeros.slice(2,7)}-${numeros.slice(7,11)}`;
  };

  // Formata sempre como CPF (000.000.000-00)
  const formatarCPFCNPJ = (valor) => {
    // Limita a 11 dígitos
    const numeros = valor.replace(/\D/g, '').slice(0, 11);
    if (numeros.length === 0) return '';
    if (numeros.length <= 3) return numeros;
    if (numeros.length <= 6) return numeros.replace(/(\d{3})(\d+)/, '$1.$2');
    if (numeros.length <= 9) return numeros.replace(/(\d{3})(\d{3})(\d+)/, '$1.$2.$3');
    return numeros.replace(/(\d{3})(\d{3})(\d{3})(\d{1,2})/, '$1.$2.$3-$4');
  };

  const handleSubmit = async () => {
    try {
      if (editando) {
        await pacienteService.atualizar(editando.id, formData);
        setSuccess('Paciente atualizado com sucesso!');
      } else {
        await pacienteService.criar(formData);
        setSuccess('Paciente cadastrado com sucesso!');
      }
      
      handleCloseModal();
      carregarPacientes(page, pesquisa);

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Erro ao salvar paciente');
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Deseja realmente excluir este paciente?')) {
      try {
        await pacienteService.deletar(id);
        setSuccess('Paciente excluído com sucesso!');
        carregarPacientes(page, pesquisa);
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError('Erro ao excluir paciente');
        console.error(err);
      }
    }
  };

  const limitePacientesFree = 50;
  const atingiuLimiteFree = planoAtual === 'FREE' && total >= limitePacientesFree;

  return (
    <Box sx={{ p: 3, backgroundColor: isDarkMode ? undefined : '#FFFAF5', minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ borderLeft: '4px solid #F97316', pl: 1.5 }}>
            Cadastro de Pacientes
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, pl: 1.5 }}>
            Cadastros auxiliares para facilitar os lançamentos
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenModal()}
          disabled={atingiuLimiteFree}
          sx={{
            bgcolor: '#F97316',
            '&:hover': { bgcolor: '#EA580C' },
            boxShadow: '0 2px 10px rgba(249,115,22,0.35)',
            '&.Mui-disabled': { bgcolor: 'action.disabledBackground' }
          }}
        >
          Novo Paciente
        </Button>
      </Box>
      {atingiuLimiteFree && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Limite de 50 pacientes atingido no plano grátis. Para cadastrar mais pacientes, faça upgrade para o plano Pro.
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <TextField
        fullWidth
        placeholder="Pesquisar por nome ou CPF/CNPJ..."
        value={pesquisa}
        onChange={(e) => setPesquisa(e.target.value)}
        InputProps={{
          startAdornment: <SearchIcon sx={{ mr: 1, color: '#F97316' }} />
        }}
        sx={{
          mb: 3,
          '& .MuiOutlinedInput-root': {
            '&:hover fieldset': { borderColor: 'rgba(249,115,22,0.5)' },
            '&.Mui-focused fieldset': { borderColor: '#F97316' },
          }
        }}
      />

      <TableContainer component={Paper} sx={{
        borderTop: '3px solid #F97316',
        boxShadow: '0 2px 12px rgba(249,115,22,0.08)',
        background: isDarkMode ? undefined : '#fff',
      }}>
        <Table>
          <TableHead>
            <TableRow sx={{ background: isDarkMode ? 'rgba(249,115,22,0.08)' : 'rgba(249,115,22,0.06)' }}>
              <TableCell sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.72rem', color: '#F97316', letterSpacing: '0.5px' }}>Nome</TableCell>
              <TableCell sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.72rem', color: '#F97316', letterSpacing: '0.5px' }}>CPF/CNPJ</TableCell>
              <TableCell sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.72rem', color: '#F97316', letterSpacing: '0.5px' }}>Contato</TableCell>
              <TableCell sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.72rem', color: '#F97316', letterSpacing: '0.5px' }}>Status</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.72rem', color: '#F97316', letterSpacing: '0.5px' }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">Carregando...</TableCell>
              </TableRow>
            ) : pacientes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">Nenhum paciente encontrado</TableCell>
              </TableRow>
            ) : (
              pacientes.map((paciente) => (
                <TableRow
                  key={paciente.id}
                  hover
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/pacientes/${paciente.id}`)}
                  sx={{
                    borderBottom: '1px solid rgba(249,115,22,0.1)',
                    '&:hover': { backgroundColor: 'rgba(249,115,22,0.04) !important' }
                  }}
                >
                  <TableCell sx={{ fontWeight: 500 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      {paciente.nome}
                      {!paciente.logradouro && (
                        <Tooltip title="Paciente sem endereço cadastrado — a nota fiscal sairá sem endereço">
                          <WarningAmberIcon sx={{ fontSize: 18, color: '#F59E0B' }} />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{paciente.cpfCnpj || '-'}</TableCell>
                  <TableCell>
                    {paciente.telefone && <div>{paciente.telefone}</div>}
                    {paciente.email && <div style={{ fontSize: '0.85em', color: '#888' }}>{paciente.email}</div>}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={paciente.ativo ? 'Ativo' : 'Inativo'}
                      size="small"
                      sx={paciente.ativo ? {
                        bgcolor: 'rgba(34,197,94,0.12)',
                        color: '#16A34A',
                        fontWeight: 600,
                        fontSize: '0.72rem'
                      } : {
                        fontWeight: 600,
                        fontSize: '0.72rem'
                      }}
                    />
                  </TableCell>
                  <TableCell align="right" onClick={e => e.stopPropagation()}>
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => handleOpenModal(paciente)} sx={{ color: '#F97316' }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton size="small" onClick={() => handleDelete(paciente.id)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {total} paciente{total !== 1 ? 's' : ''} encontrado{total !== 1 ? 's' : ''}
          </Typography>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            sx={{
              '& .MuiPaginationItem-root.Mui-selected': { bgcolor: '#F97316', color: '#fff' },
              '& .MuiPaginationItem-root:hover': { bgcolor: 'rgba(249,115,22,0.12)' }
            }}
          />
        </Box>
      )}

      {/* Modal de Cadastro/Edição */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: isDarkMode
            ? { background: 'rgba(30,41,59,0.92)', backdropFilter: 'blur(12px)', border: '1px solid rgba(249,115,22,0.2)' }
            : { background: '#FFFAF5', border: '1px solid rgba(249,115,22,0.2)' }
        }}
      >
        <DialogTitle sx={{ borderLeft: '4px solid #F97316', pl: 2, fontWeight: 700 }}>
          {editando ? 'Editar Paciente' : 'Novo Paciente'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={12}>
              <TextField
                fullWidth
                label="Nome *"
                name="nome"
                value={formData.nome}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="CPF"
                name="cpfCnpj"
                value={formData.cpfCnpj}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Telefone"
                name="telefone"
                value={formData.telefone}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="E-mail"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Data de Nascimento"
                name="dataNascimento"
                type="date"
                value={formData.dataNascimento || ''}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: '#F97316', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: '0.5px', borderLeft: '3px solid #F97316', pl: 1 }}>Endereço</Typography>
            </Grid>
            {!formData.logradouro && (
              <Grid item xs={12}>
                <Alert severity="warning" icon={<WarningAmberIcon fontSize="small" />} sx={{ py: 0.5 }}>
                  Paciente sem endereço cadastrado — a nota fiscal sairá sem endereço.
                </Alert>
              </Grid>
            )}

            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="CEP"
                name="cep"
                value={formData.cep}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={7}>
              <TextField
                fullWidth
                label="Logradouro"
                name="logradouro"
                value={formData.logradouro}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                label="Número"
                name="numero"
                value={formData.numero}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Complemento"
                name="complemento"
                value={formData.complemento}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Bairro"
                name="bairro"
                value={formData.bairro}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={10}>
              <TextField
                fullWidth
                label="Cidade"
                name="cidade"
                value={formData.cidade}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                label="UF"
                name="estado"
                value={formData.estado}
                onChange={handleChange}
                inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Observações"
                name="observacoes"
                value={formData.observacoes}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseModal} sx={{ color: 'text.secondary' }}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!formData.nome}
            sx={{
              bgcolor: '#F97316',
              '&:hover': { bgcolor: '#EA580C' },
              boxShadow: '0 2px 10px rgba(249,115,22,0.35)',
              '&.Mui-disabled': { bgcolor: 'action.disabledBackground' }
            }}
          >
            {editando ? 'Atualizar' : 'Cadastrar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Pacientes;
